import request from '../utils/request';
import {
  IIncident,
  IMaxOdd,
  ITournamentStage,
  IEventCompetitor,
  IEventProperty,
  IWidgetProvider,
  IWidgetMeta,
  IWidget,
  IRegion,
  IBuilderComponentGroup,
  ILineup,
  IPrevEvent,
  ICompetitor,
  IEvent,
  IChannel,
  IBannerAdObj,
  ISuspensions,
  IInjury,
  IWidgetBackground,
  INextRoundData,
  EventStatus,
  IWidgetCompetition,
  ITournament,
  IFeaturedEvent,
  IProvider,
  IWidgetConfiguration,
  IObjectStyle,
  IOdds as IOddsMain,
  IOfferDiskDataResponse,
  EventPositions,
  ILineupPlayerType,
  ILineupPlayer,
  ICoachData,
  IEventContent,
  BuilderComponentSlug,
  IGameMinutesResponse
} from "../utils/types";
import {ITranslation} from "../utils/translations";

interface IHeaderIncidents {
  position: number;
  incidents: IIncident[];
}

interface IPeriodScore {
  period: string;
  score: number[];
}

export interface IEventDetails {
  cityName: string | null;
  venueName: string | null;
  venueNeutral: boolean;
  refereeName: string | null;
  region: IRegion;
  forecast: IForecast | null;
  channels: IChannel[];
}

export interface IForecast {
  icon: number;
  temperature: number;
  humidity: number;
  wind: {
    speed: number;
    direction: string;
  }
}

export interface IOutLinks {
  channelOutLinkFolder: string | null;
  outLinkFolder: string | null;
}

export interface IWidgetResponse extends IWidget {
  translations: ITranslation[];
  widgetMetas: IWidgetMeta[];
  widgetProviders: IWidgetProvider[];
  builderComponentGroup: IBuilderComponentGroup[];
  outLinks: IOutLinks;
  widgetStyles: IObjectStyle[],
  config: IExternalWidgetConfig;
  backgrounds: null | IWidgetBackground[];
  widgetCompetitions: IWidgetCompetition[];
  tournaments: ICompetitionTournaments[];
  channel: IChannel | null;
  channelOutLinkFolder: string | null;
  configuration: null | IWidgetConfiguration[];
  widgetTypeId: number;
  client: IClient;
  componentEventContent?: IComponentEventContent[];
}

export interface IComponentEventContent {
  componentSlug: BuilderComponentSlug;
  content: IEventContent[];
  onlyEventContent: boolean;
}

export interface IClient {
  id: number;
  userId: number | null;
  name: string;
  regionIds: number[];
}

export interface IEventBannerDataResponse extends IEvent {
  tournamentStageId: number;
  gameMoment: string | null;
  score: null | number[];
  aet: boolean;
  penalties: null | number[];
  periodScores: IPeriodScore[];
  headerIncidents: IHeaderIncidents[];
  maxOdds: IMaxOdd[];
  maxOddsMarketId: null | number;
  tournamentStage: ITournamentStage;
  eventCompetitors: IEventCompetitor[];
  properties: IEventProperty[];
  sportLink: null | { link: string, text: string };
  leagueLink: null | { link: string, text: string };
}

export interface IEventLiveDataResponse {
  status: EventStatus;
  gameMoment: string | null;
  score: null | number[];
  penalties: null | number[];
  periodScores: IPeriodScore[];
  headerIncidents: IHeaderIncidents[];
  maxOdds: IMaxOdd[];
  eventCompetitors: IEventCompetitor[];
}

export interface IExtendedLineup extends ILineup {
  position: EventPositions;
  mainLineupPlayerTypes: ILineupPlayerType[];
  subs: ILineupPlayer[];
  kitImgSrc: string | null;
  pitchMap: ILineupPlayer[][] | null[][] | null;
  coachData: ICoachData | null;
}

export interface ILineupsDataResponse {
  success: boolean;
  data?: {
    lineups: IExtendedLineup[];
    venueName: string | null;
    venueNeutral: boolean | null;
    cityName: string | null;
    region: IRegion | null;
    refereeName: string | null;
  }
}

export interface ILineupsPitchDataResponse {
  success: boolean;
  data?: {
    lineups: IExtendedLineup[];
    venueName: string | null;
    venueNeutral: boolean | null;
    cityName: string | null;
    region: IRegion | null;
    refereeName: string | null;
  }
}

export interface IHead2HeadEvent extends IEvent {
  score: number[],
  tournamentStage: ITournamentStage,
  eventCompetitors: IEventCompetitor[],
}

export interface IEventHead2HeadDataResponse {
  firstEntry: string | null;
  score?: null | number[];
  draw: null | number;
  won: null | number;
  lost: null | number;
  games: null | number;
  events: IHead2HeadEvent[];
  eventCompetitors: IEventCompetitor[];
  sportId: number;
}

export interface IStandingsPosition {
  color: string;
  code: string;
}

export interface IStandingRow {
  id: number;
  rank: number;
  prevRank: number | null;
  color: string | null;
  played: number | null;
  wins: number | null;
  draws: number | null;
  defeits: number | null;
  points: number | null;
  goalsfor: number | null;
  goalsagainst: number | null;
  goalsdiff: number | null;
  competitor: ICompetitor;
  prevMeetings: IPrevEvent[];
  losses: number | null;
  nextEvent: INextEvent | null;
  lastResults: string[] | null;
  tournamentStageId: number;
}

export interface INextEvent extends IEvent {
  channelId: number;
  channelName: string;
  channelLogoUrl: string;
  channelSlug: string;
  opponentName: string;
  opponentLogoUrl: string;
  opponentPosition: number;
  odds: IOdds[];
}

export interface IByTournamentStage {
  competitors: IStandingRow[]
  id: number
  name: string
  gender?: string
  startTime: string
  endTime: string
  regionId: number
  tournamentId: number
}

export interface IStandingsResponse {
  eventCompetitors: IEventCompetitor[];
  table: {
    rows: IStandingRow[];
    byTournamentStage: IByTournamentStage[]
    positions: IStandingsPosition[];
  }
}

export interface ITopScorerPlayer {
  goals: number;
  played: number;
  assists: number;
  playerInfo: ICompetitor;
}

export interface ITopScorersResponse {
  homeTeam: {
    competitor: ICompetitor;
    players: ITopScorerPlayer[];
  }
  awayTeam: {
    competitor: ICompetitor;
    players: ITopScorerPlayer[];
  }
}

export interface IEventOddsInfoResponse {
  status: EventStatus,
  sportId: number
}

export interface IMatchStatisticsResponse {
  success: boolean;
  event: IEvent | null;
}

export interface IInjuriesAndSuspensions {
  eventCompetitor: IEventCompetitor;
  injuries: IInjury[],
  suspensions: ISuspensions[];
}

export interface IInjuriesAndSuspensionsResponse {
  success: boolean,
  message?: string,
  injuriesAndSuspensions?: IInjuriesAndSuspensions[],
}

export interface ITrends {
  odds: IOdds[];
  maxOdds?: IOdds;
  trend: string;
  title: string;
  marketTypeId: number
}

export interface ITrendGroup {
  competitorId: number;
  trends: ITrends[];
}

export interface ITrendsResponse {
  event: IEvent;
  trends: ITrendGroup[];
}

export interface IFallbackResponse extends IEvent {
  tournamentStage: ITournamentStage;
  eventCompetitors: IEventCompetitor[];
}

export interface IAxSemanticsResponse {
  success: boolean;
  axSemanticsHtml: string | null;
}

export interface IExternalWidgetConfig {
  logoUrl: string;
  url: string;
}

export interface IProviderFallbackAffiliateLinksResponse {
  success: boolean;
  fallbackClientAffiliateLinks: IFallbackClientAffiliateLink[]
}

export interface IFallbackClientAffiliateLink {
  outLinkFolder: string;
  customProviderSlug: string | null;
  provider: IProvider
}

export interface IFeaturedEventsResponse {
  tournament: ITournament
  featuredEvents: IFeaturedEvent[]
}

export interface IChannelsResponse {
  success: boolean;
  message?: string;
  recommended: {
    channel: null | IChannel
    valueProps?: string //json string
  };
  streams: IChannel[];
  broadcasts: IChannel[];
}

export interface ICompetitionTournaments {
  competitionId: number;
  data: {
    tournamentId: number;
    tournamentName: string;
    stages: {
      id: number,
      name: string
    }[];
  }[]
}

export interface IOdds extends IOddsMain {
}

export interface IMatchPredictionResponse {
  odds: IOdds[],
  probability: number,
  eventCompetitor: IEventCompetitor,
  eventOpponent: IEventCompetitor,
  bestOdds: IOdds
  title: string
}

export function fetchWidgetData(id: string | number | undefined, eventId: string | number | undefined = undefined) {
  return request(`/api/match-center/widget-data/${id}/${eventId ?? 0}`, {}) as unknown as Promise<IWidgetResponse>;
}

export function fetchOfferDiskData(widgetId: number) {
  return request(`api/offer-disks/${widgetId}`, {}) as unknown as Promise<IOfferDiskDataResponse>;
}

export function fetchGameMinutes(query: Object) {
  return request(`/api/match-center/game-minutes`, query) as unknown as Promise<IGameMinutesResponse[]>
}

export function fetchBannerData(query: Object) {
  return request(`/api/match-center/banner-data`, query) as unknown as Promise<IEventBannerDataResponse>;
}

export function updateLiveEventData(query: Object) {
  return request(`/api/match-center/update-live-event-data`, query) as unknown as Promise<IEventLiveDataResponse>;
}

export function fetchEventDetails(query: Object) {
  return request(`/api/match-center/event-details`, query) as unknown as Promise<IEventDetails>;
}

export function fetchHead2Head(query: Object) {
  return request(`/api/match-center/head-2-head`, query) as unknown as Promise<IEventHead2HeadDataResponse>;
}

export function fetchLineupsData(query: Object) {
  return request(`/api/match-center/lineups/data`, query) as unknown as Promise<ILineupsDataResponse>;
}

export function fetchLineupsPitchMap(query: Object) {
  return request(`/api/match-center/lineups/pitch-map`, query) as unknown as Promise<ILineupsPitchDataResponse>;
}

export function fetchStandingsData(query: Object) {
  return request(`/api/match-center/standings-data`, query) as unknown as Promise<IStandingsResponse>;
}

export function fetchTopScorersData(query: Object) {
  return request(`/api/match-center/top-scorers`, query) as unknown as Promise<ITopScorersResponse>;
}

export function getOddsEventInfo(query: Object) {
  return request(`/api/match-center/odds-event-info`, query) as unknown as Promise<IEventOddsInfoResponse>;
}

export function fetchInjuriesAndSuspensions(query: Object) {
  return request(`/api/match-center/injuries-and-suspensions`, query) as unknown as Promise<IInjuriesAndSuspensionsResponse>;
}

export function fetchMatchCenterStatistics(query: Object) {
  return request(`/api/match-center/statistics`, query) as unknown as Promise<IMatchStatisticsResponse>;
}

export function fetchMatchPrediction(query: Object) {
  return request(`/api/match-center/match-prediction`, query) as unknown as Promise<IMatchPredictionResponse>;
}

export function fetchEvent(eventId: Number) {
  return request(`/api/match-center/event/${eventId}`) as unknown as Promise<IEvent>;
}

export function fetchTrends(query: Object) {
  return request(`/api/match-center/trends`, query) as unknown as Promise<ITrendsResponse>;
}

export function fetchBanners(widgetId: Number) {
  return request(`/api/match-center/get-banners`, {widgetId: widgetId}) as unknown as Promise<IBannerAdObj[]>;
}

export function fetchFallbackData(query: Object) {
  return request(`/api/match-center/fallback`, query) as unknown as Promise<IFallbackResponse>;
}

export function fetchAxSemanticsData(query: Object) {
  return request(`/api/match-center/ax-semantics`, query) as unknown as Promise<IAxSemanticsResponse>;
}

export function fetchProviderFallbackAffiliateLinksData(query: Object) {
  return request(`/api/match-center/provider-fallback-affiliate-links-data`, query) as unknown as Promise<IProviderFallbackAffiliateLinksResponse>;
}

export function fetchChannelsData(query: Object) {
  return request(`/api/match-center/channels`, query) as unknown as Promise<IChannelsResponse>;
}

export function fetchNextRoundData(query: object) {
  return request(`/api/match-center/next-round`, query) as unknown as Promise<INextRoundData>;
}

export function fetchEventContentData(query: object) {
  return request(`/api/match-center/event-content`, query) as unknown as Promise<IEventContent>;
}

